import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Tabs.scss';

/**
 * Hook to detect mobile viewport
 */
const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    setIsMobile(mql.matches);
    return () => mql.removeEventListener('change', handler);
  }, [breakpoint]);

  return isMobile;
};

const ChevronIcon = ({ isOpen }) => (
  <svg
    width="16" height="16" viewBox="0 0 16 16" fill="none"
    className={`custom-tabs-chevron ${isOpen ? 'open' : ''}`}
  >
    <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/**
 * Custom Tabs Component with theme support
 * Matches UX_DESIGNS shadcn/ui tab styling
 * Responsive: auto-switches to dropdown on mobile for 4+ tabs
 */

export const Tabs = ({ defaultValue, value, onChange, children, className = '', ...props }) => {
  const [internalTab, setInternalTab] = useState(defaultValue);
  const activeTab = value !== undefined ? value : internalTab;
  const setActiveTab = useCallback((val) => {
    if (value === undefined) setInternalTab(val);
    if (onChange) onChange(val);
  }, [value, onChange]);

  const childrenArray = React.Children.toArray(children);
  const tabsList = childrenArray.find(child => child.type === TabsList);
  const tabsContents = childrenArray.filter(child => child.type === TabsContent);

  return (
    <div className={`custom-tabs ${className}`} {...props}>
      {tabsList && React.cloneElement(tabsList, { activeTab, setActiveTab })}
      {tabsContents.map(content => 
        React.cloneElement(content, { 
          key: content.props.value,
          isActive: content.props.value === activeTab 
        })
      )}
    </div>
  );
};

export const TabsList = ({ children, className = '', activeTab, setActiveTab, mobileThreshold = 4 }) => {
  const isMobile = useIsMobile();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const handleSelect = useCallback((value) => {
    setActiveTab(value);
    setDropdownOpen(false);
  }, [setActiveTab]);

  // Extract tab trigger children
  const tabs = React.Children.toArray(children).filter(
    child => child?.type === TabsTrigger
  );

  const activeLabel = tabs.find(tab => tab.props.value === activeTab)?.props.children || 'Select';

  // Dropdown mode: on mobile when tabs exceed threshold (default 3)
  if (isMobile && tabs.length > 3) {
    return (
      <div className={`custom-tabs-dropdown ${className}`} ref={dropdownRef}>
        <button
          type="button"
          className={`custom-tabs-dropdown-trigger ${dropdownOpen ? 'open' : ''}`}
          onClick={() => setDropdownOpen(prev => !prev)}
          aria-expanded={dropdownOpen}
          aria-haspopup="listbox"
        >
          <span className="custom-tabs-dropdown-label">{activeLabel}</span>
          <ChevronIcon isOpen={dropdownOpen} />
        </button>
        {dropdownOpen && (
          <div className="custom-tabs-dropdown-menu" role="menu">
            {tabs.map(tab => (
              <button
                key={tab.props.value}
                type="button"
                role="menuitemradio"
                aria-checked={tab.props.value === activeTab}
                className={`custom-tabs-dropdown-item ${tab.props.value === activeTab ? 'active' : ''}`}
                onClick={() => handleSelect(tab.props.value)}
              >
                {tab.props.children}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`custom-tabs-list ${className}`} role="tablist">
      {React.Children.map(children, child =>
        child ? React.cloneElement(child, { activeTab, setActiveTab }) : null
      )}
    </div>
  );
};

export const TabsTrigger = ({ value, children, className = '', activeTab, setActiveTab }) => {
  const isActive = value === activeTab;
  
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      className={`custom-tabs-trigger ${isActive ? 'active' : ''} ${className}`}
      onClick={() => setActiveTab(value)}
    >
      {children}
    </button>
  );
};

export const TabsContent = ({ value, children, className = '', isActive }) => {
  if (!isActive) return null;
  
  return (
    <div
      role="tabpanel"
      className={`custom-tabs-content ${className}`}
    >
      {children}
    </div>
  );
};
